import { type FC, useState } from 'react';
import { Column } from '~/commonComponents';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { ProcedureOption } from './ProcedureOption';
import { Colors } from '~/themes/colors';

const MAX_PROCEDURES = 4;

interface IExpectedProceduresPopupProps {
  procedures: string[];
  selected: string[];
  onConfirm: (selected: string[]) => void;
  onClose: () => void;
}

export const ExpectedProceduresPopup: FC<IExpectedProceduresPopupProps> = ({
  procedures,
  selected,
  onConfirm,
  onClose,
}) => {
  const [localSelected, setLocalSelected] = useState<string[]>(selected);

  const handleToggle = (procedure: string) => {
    setLocalSelected((prev) => {
      if (prev.includes(procedure)) {
        return prev.filter((p) => p !== procedure);
      }
      if (prev.length < MAX_PROCEDURES) {
        return [...prev, procedure];
      }
      return prev;
    });
  };

  const hasSelections = localSelected.length > 0;

  const handleConfirm = () => {
    if (hasSelections) {
      onConfirm(localSelected);
    }
  };

  return (
    // @ts-expect-error ConfirmPopup component still written in JavaScript
    <ConfirmPopup
      title="Expected Procedures"
      description="Select up to 4 procedures"
      showClosePopupIcon
      closePopup={onClose}
      rightButtonText={hasSelections ? `${localSelected.length} Selected` : 'Select'}
      rightButtonAction={handleConfirm}
      rightButtonDisabled={!hasSelections}
      descriptionStyle={{ marginBottom: 20, marginTop: -8 }}
      dismissiblePopup={false}
      modalStyle={{ height: 500, padding: '45px 10px' }}
      dataTestId="procedures-popup-overlay"
    >
      <Column
        data-testid="procedures-list"
        data-cy="procedures-list"
        gap={10}
        alignItems="center"
        style={{
          flex: 1,
          overflow: 'auto',
          overscrollBehavior: 'contain',
          marginBottom: 24,
          scrollbarWidth: 'thin',
          scrollbarColor: `${Colors.neutral_divider} ${Colors.white}`,
        }}
      >
        {procedures.map((procedure) => (
          <ProcedureOption
            key={procedure}
            label={procedure.split('_')[1]}
            isSelected={localSelected.includes(procedure)}
            isDisabled={
              localSelected.length >= MAX_PROCEDURES && !localSelected.includes(procedure)
            }
            onToggle={() => handleToggle(procedure)}
            testId={`procedure-option-${procedure}`}
          />
        ))}
      </Column>
    </ConfirmPopup>
  );
};
